import type { Player } from '../types'
import { playerCurrentWeaponSlugs } from './playerWeaponBuilds'

export const PlayerFilterMode = {
  Filter: 'filter',
  Highlight: 'highlight',
} as const

export type PlayerFilterMode = (typeof PlayerFilterMode)[keyof typeof PlayerFilterMode]

export interface PlayerFilters {
  readonly name: string
  readonly weaponSlugs: ReadonlyArray<string>
  readonly tagIds: ReadonlyArray<string>
}

export function emptyPlayerFilters(): PlayerFilters {
  return {
    name: '',
    weaponSlugs: [],
    tagIds: [],
  }
}

export function copyPlayerFilters(filters: PlayerFilters): PlayerFilters {
  return {
    name: filters.name,
    weaponSlugs: [...filters.weaponSlugs],
    tagIds: [...filters.tagIds],
  }
}

export function playerFilterCount(filters: PlayerFilters): number {
  return (filters.name.trim() === '' ? 0 : 1)
    + filters.weaponSlugs.length
    + filters.tagIds.length
}

export function playerFiltersAreActive(filters: PlayerFilters): boolean {
  return playerFilterCount(filters) > 0
}

export function playerMatchesNameAndTags(player: Player, filters: PlayerFilters): boolean {
  const nameQuery = filters.name.trim().toLowerCase()
  if (
    nameQuery !== ''
    && !player.discordName.toLowerCase().includes(nameQuery)
    && !player.guildNickname.toLowerCase().includes(nameQuery)
    && !player.playerNickname.toLowerCase().includes(nameQuery)
    && !player.playerAccountName.toLowerCase().includes(nameQuery)
    && !player.inGameName.toLowerCase().includes(nameQuery)
  ) {
    return false
  }

  if (
    filters.tagIds.length > 0
    && !filters.tagIds.some((tagId) => player.tags.some((tag) => tag.id === tagId))
  ) {
    return false
  }

  return true
}

/**
 * Categories combine with AND. Multiple values inside Weapons or Tags combine
 * with OR, so selecting two weapons finds players using either one while an
 * additional tag still narrows that result.
 */
export function playerMatchesFilters(player: Player, filters: PlayerFilters): boolean {
  if (!playerMatchesNameAndTags(player, filters)) return false
  if (filters.weaponSlugs.length === 0) return true

  const currentWeapons = new Set(playerCurrentWeaponSlugs(player))
  return filters.weaponSlugs.some((slug) => currentWeapons.has(slug))
}
